<script>
    import { navigationStore } from '../../../stores/navigationStore';
    import { documentStructureService } from '../../../services/documentStructureService';

    export let item;
    export let editor;
    export let level = 0;

    let isExpanded = false;
    let isActive = false;
    let currentHeadingId = null;

    // Subscribe to navigation store for expansion and current heading state
    navigationStore.subscribe(state => {
        isExpanded = state.expandedSections.has(item.id);
        currentHeadingId = state.currentHeadingId;
        isActive = currentHeadingId === item.id;
    });

    // Check if item has children
    $: hasChildren = item.children && item.children.length > 0;

    // Calculate indentation
    $: indentStyle = `padding-left: ${16 + (level * 16)}px`;

    // Handle click
    function handleClick(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Navigate to heading
        documentStructureService.navigateToPosition(editor, item.position);
        
        // Update current heading
        navigationStore.updateCurrentHeading(item.id);
    }

    // Handle expand/collapse
    function handleToggle(e) {
        e.preventDefault();
        e.stopPropagation();
        navigationStore.toggleSection(item.id);
    }

    // Get heading level class
    function getHeadingClass(level) {
        return `heading-level-${level}`;
    }

    // Get section type label
    function getSectionLabel(sectionType) {
        if (!sectionType) return '';
        return documentStructureService.getSectionDisplayName(sectionType);
    }
</script>

<div class="navigation-item-wrapper">
    <button
        class="navigation-item {getHeadingClass(item.level)}"
        class:active={isActive}
        class:has-children={hasChildren}
        style={indentStyle}
        on:click={handleClick}
        role="treeitem"
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isActive}
        title={item.text}
    >
        {#if hasChildren}
            <button
                class="expand-button"
                on:click={handleToggle}
                aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            >
                <svg 
                    class="expand-icon" 
                    class:expanded={isExpanded}
                    width="12" 
                    height="12" 
                    viewBox="0 0 12 12"
                >
                    <path 
                        d="M4 5L7 8L10 5" 
                        stroke="currentColor" 
                        stroke-width="1.5" 
                        stroke-linecap="round"
                        fill="none"
                    />
                </svg>
            </button>
        {:else}
            <span class="indent-spacer"></span>
        {/if}
        
        <span class="item-text">
            {item.text}
            {#if item.sectionType && item.level === 2}
                <span class="section-type">({getSectionLabel(item.sectionType)})</span>
            {/if}
        </span>
    </button>

    {#if hasChildren && isExpanded}
        <div class="navigation-tree" role="tree" data-level={level + 1}>
            {#each item.children as child (child.id)}
                <svelte:self item={child} {editor} level={level + 1} />
            {/each}
        </div>
    {/if}
</div>

<style>
    .navigation-item-wrapper {
        display: flex;
        flex-direction: column;
    }
    
    .navigation-tree {
        display: flex;
        flex-direction: column;
    }

    .navigation-tree[data-level="0"] {
        padding: 0;
    }

    .navigation-item {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 32px;
        padding: 6px var(--spacing-4);
        font-size: var(--font-size-sm);
        line-height: 1.4;
        text-align: left;
        color: var(--color-text-secondary);
        background: transparent;
        border: none;
        transition: all var(--transition-colors);
        cursor: pointer;
    }

    .navigation-item:hover {
        color: var(--color-text-primary);
        background: var(--color-btn-spec);
    }

    .navigation-item.active {
        font-weight: var(--font-weight-medium);
        color: var(--color-text-primary);
        background: var(--color-btn-spec-hover);
    }

    .navigation-item.active::before {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 3px;
        background: var(--color-spec-figure-text);
        content: '';
    }

    /* Heading level styles */
    .navigation-item.heading-level-1 {
        font-size: var(--font-size-base);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
    }

    .navigation-item.heading-level-2 {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
    }

    .navigation-item.heading-level-3 {
        font-size: var(--font-size-sm);
    }

    .navigation-item.heading-level-4 {
        font-size: var(--font-size-xs);
        font-style: italic;
    }

    .expand-button {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        padding: 0;
        margin-right: var(--spacing-1);
        color: var(--color-text-tertiary);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        cursor: pointer;
    }

    .expand-button:hover {
        color: var(--color-text-secondary);
        background: var(--color-btn-spec);
    }

    .expand-icon {
        transition: transform var(--transition-transform);
        transform: rotate(-90deg);
    }

    .expand-icon.expanded {
        transform: rotate(0deg);
    }

    .indent-spacer {
        flex-shrink: 0;
        width: 24px;
    }

    .item-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .section-type {
        margin-left: 6px;
        font-size: 11px;
        font-weight: var(--font-weight-normal);
        color: var(--color-text-tertiary);
        font-style: italic;
    }

    /* Focus styles for accessibility */
    .navigation-item:focus {
        outline: 2px solid var(--color-spec-figure-text);
        outline-offset: -2px;
        opacity: 0.5;
    }

    .expand-button:focus {
        outline: 2px solid var(--color-spec-figure-text);
        outline-offset: -2px;
        opacity: 0.5;
    }
</style>